import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { database, auth } from './firebase';
import { signInAnonymously } from 'firebase/auth';
import { ref, set, get, onValue, serverTimestamp } from 'firebase/database';
import { Html5QrcodeScanner } from 'html5-qrcode';

function TabletLogin() {
  const navigate = useNavigate();
  const [mode, setMode] = useState('select'); // 'select', 'scan', 'manual'
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const qrScannerInstance = useRef(null);
  
  // Responsive helper - visual only
  const isMobile = window.innerWidth < 768;

  useEffect(() => {
    // Check if already logged in
    const storedTableId = localStorage.getItem('tabletTableId');
    if (storedTableId) {
      // Verify table still exists in Firebase
      const tableRef = ref(database, `tablets/${storedTableId}`);
      onValue(tableRef, (snapshot) => {
        if (snapshot.exists()) {
          navigate(`/tablet/${storedTableId}`);
        } else {
          localStorage.removeItem('tabletTableId');
        }
      }, { onlyOnce: true });
    }
  }, [navigate]);

  useEffect(() => {
    if (mode === 'scan' && !qrScannerInstance.current) {
      const scanner = new Html5QrcodeScanner(
        "qr-reader",
        { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1.0 },
        false
      );

      scanner.render(
        async (decodedText) => {
          try {
            const data = JSON.parse(decodedText);
            if (data.type === 'table-login' && data.tableId && data.scoreboardCode) {
              await loginWithCode(data.scoreboardCode, data);
            } else {
              setError('Invalid QR code. Please scan a table QR code.');
            }
          } catch (err) {
            setError('Could not read QR code. Please try manual entry.');
            console.error('QR scan error:', err);
          }
          scanner.clear();
        },
        () => {}
      );

      qrScannerInstance.current = scanner;
    }

    return () => {
      if (qrScannerInstance.current) {
        qrScannerInstance.current.clear().catch(err => console.log('Scanner cleanup:', err));
        qrScannerInstance.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    
    if (!code.trim()) {
      setError('Please enter a table code');
      return;
    }

    if (code.length !== 8) {
      setError('Table code must be 8 characters');
      return;
    }

    await loginWithCode(code.toLowerCase().trim());
  };

  const loginWithCode = async (scoreboardCode, qrData = null) => {
    setLoading(true);
    setError('');

    try {
      // Sign in anonymously so Firebase security rules can verify auth != null
      if (!auth.currentUser) {
        await signInAnonymously(auth);
      }

      // If QR data provided, we have all the info we need
      if (qrData) {
        await registerTablet(qrData.tableId, {
          tableNumber: qrData.tableNumber,
          venueName: qrData.venueName,
          scoreboardCode: scoreboardCode,
          organiserId: qrData.organiserId,
        });
        return;
      }

      // For manual entry, look up the scoreboard code in the index
      const codeRef = ref(database, `scoreboardCodes/${scoreboardCode}`);
      const codeSnapshot = await get(codeRef);
      
      if (codeSnapshot.exists()) {
        const data = codeSnapshot.val();
        await registerTablet(data.tableId, {
          tableNumber: data.tableNumber,
          venueName: data.venueName,
          scoreboardCode: scoreboardCode,
          organiserId: data.organiserId,
        });
        return;
      }

      setError('Invalid table code. Please check the code and try again.');
      setLoading(false);
      
    } catch (err) {
      console.error('Login error:', err);
      setError('Failed to login. Please try again.');
      setLoading(false);
    }
  };

  const registerTablet = async (tableId, info) => {
    try {
      // Register this tablet with the table
      const tabletRef = ref(database, `tablets/${tableId}`);
      await set(tabletRef, {
        tableNumber: info.tableNumber,
        venueName: info.venueName,
        scoreboardCode: info.scoreboardCode,
        organiserId: info.organiserId,
        status: 'online',
        lastPing: serverTimestamp(),
        deviceInfo: {
          userAgent: navigator.userAgent,
          screenWidth: window.screen.width,
          screenHeight: window.screen.height,
        },
        currentMatch: null,
        loginTime: serverTimestamp(),
      });

      // Save to localStorage
      localStorage.setItem('tabletTableId', tableId);
      
      // Start heartbeat
      startHeartbeat(tableId);

      // Navigate to tablet waiting screen
      navigate(`/tablet/${tableId}`);
      
    } catch (err) {
      console.error('Registration error:', err);
      setError('Failed to register tablet. Please try again.');
      setLoading(false);
    }
  };

  const startHeartbeat = (tableId) => {
    // Send heartbeat every 30 seconds
    const heartbeatInterval = setInterval(() => {
      const tabletRef = ref(database, `tablets/${tableId}/lastPing`);
      set(tabletRef, serverTimestamp());
    }, 30000);

    // Store interval ID so we can clear it later
    localStorage.setItem('heartbeatInterval', heartbeatInterval);
  };

  const styles = {
    container: {
      minHeight: '100vh',
      backgroundColor: '#111827',
      color: 'white',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px',
    },
    card: {
      backgroundColor: '#1f2937',
      borderRadius: '12px',
      padding: isMobile ? '20px' : '32px',
      maxWidth: '500px',
      width: '100%',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
    },
    title: {
      fontSize: isMobile ? '24px' : '28px',
      fontWeight: 'bold',
      marginBottom: '8px',
      textAlign: 'center',
    },
    subtitle: {
      fontSize: '14px',
      color: '#9ca3af',
      marginBottom: '32px',
      textAlign: 'center',
    },
    button: {
      width: '100%',
      padding: '16px',
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: 'bold',
      cursor: 'pointer',
      marginBottom: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
    },
    buttonSecondary: {
      backgroundColor: '#374151',
    },
    qrContainer: {
      width: '100%',
      marginBottom: '16px',
      borderRadius: '8px',
      overflow: 'hidden',
    },
    input: {
      width: '100%',
      padding: '12px',
      backgroundColor: '#374151',
      border: '1px solid #4b5563',
      borderRadius: '8px',
      color: 'white',
      fontSize: '16px',
      marginBottom: '16px',
      textAlign: 'center',
      textTransform: 'lowercase',
    },
    error: {
      padding: '12px',
      backgroundColor: '#7f1d1d',
      color: '#fca5a5',
      borderRadius: '8px',
      fontSize: '14px',
      marginBottom: '16px',
      textAlign: 'center',
    },
    backButton: {
      padding: '8px 16px',
      backgroundColor: 'transparent',
      color: '#9ca3af',
      border: 'none',
      cursor: 'pointer',
      fontSize: '14px',
      marginBottom: '16px',
    },
  };

  // Mode: Select login method
  if (mode === 'select') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <h1 style={styles.title}>🎱 Cue Clock Pro</h1>
          <p style={styles.subtitle}>Tablet Login</p>

          {error && <div style={styles.error}>{error}</div>}

          <button 
            style={styles.button}
            onClick={() => setMode('scan')}
          >
            📷 Scan QR Code
          </button>

          <button 
            style={{...styles.button, ...styles.buttonSecondary}}
            onClick={() => setMode('manual')}
          >
            ⌨️ Enter Code Manually
          </button>

          <p style={{...styles.subtitle, marginTop: '24px', marginBottom: '0'}}>
            Scan the QR code shown on the organiser dashboard or enter your 8-digit table code
          </p>
        </div>
      </div>
    );
  }

  // Mode: QR Scanner
  if (mode === 'scan') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <button style={styles.backButton} onClick={() => {
            setMode('select');
            setError('');
          }}>
            ← Back
          </button>

          <h1 style={styles.title}>📷 Scan QR Code</h1>
          <p style={styles.subtitle}>Point your camera at the QR code on the organiser dashboard</p>

          {error && <div style={styles.error}>{error}</div>}

          <div id="qr-reader" style={{marginBottom: '16px'}}></div>

          <button 
            style={{...styles.button, ...styles.buttonSecondary}}
            onClick={() => setMode('manual')}
          >
            Switch to Manual Entry
          </button>
        </div>
      </div>
    );
  }

  // Mode: Manual Entry
  if (mode === 'manual') {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <button style={styles.backButton} onClick={() => setMode('select')}>
            ← Back
          </button>

          <h1 style={styles.title}>⌨️ Enter Table Code</h1>
          <p style={styles.subtitle}>Enter the 8-character code shown on your table</p>

          {error && <div style={styles.error}>{error}</div>}

          <form onSubmit={handleManualSubmit}>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="e.g., A1B2C3D4"
              maxLength={8}
              style={styles.input}
              disabled={loading}
              autoFocus
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
            />

            <button 
              type="submit"
              style={styles.button}
              disabled={loading || code.length !== 8}
            >
              {loading ? 'Logging in...' : 'Login to Table'}
            </button>
          </form>

          <p style={{...styles.subtitle, marginTop: '16px', marginBottom: '0', fontSize: '12px'}}>
            Code format: 8 letters/numbers (e.g., a1b2c3d4)
          </p>
        </div>
      </div>
    );
  }

  return null;
}

export default TabletLogin;